import lodash from 'lodash'
import dateUtil from '@/utils/dateUtil'
import i18n from '@/utils/i18n'
import store from '@/store'
import userHelper from '@/utils/userHelper'
import { aclHelper } from '@/utils/aclHelper'
import { getFloorIndex } from '@/api/buildings/request'
import { getSameBuildingUsers } from '@/api/users/request'
import { validateDlpInCompleted } from '@/api/projects/util'
import { getBuilding } from '@/api/workPermit/util'
import { projectStatus, tenantProjectStatus } from '@/utils/locale'
import { WP_ACTION_PERMS_MAP,
  WORK_PERMIT_STATUS,
  WORK_PERMIT_TYPES,
  WP_PROJECT_TYPE_MAP,
  WP_PROJECT_TYPES,
  WP_SA} from './consts'
import {
  formatWorkPermitHistories,
  formatWorkPermitHistoriesForViewer,
  WP_LIFECYCLE_COMMENT_HTML_KEY,
} from './workPermitLifecycleHistoryFormatter'

export async function getNextReviewerOptions (wpAction, data, excludeCurrent) {
  let tenancyType
  let buildingId
  if (data.isTenantProject) {
    let tenancy
    let permit = store.getters['workPermit/currentWorkPermit']
    if (permit.standalone) {
      tenancy = permit.tenancy
    } else {
      tenancy = store.getters['tenantProject/currentTenantProject'].tenancy
    }
    tenancyType = tenancy.type
    buildingId = tenancy.building.id
  } else {
    buildingId = data.buildingId
  }
  const perm = WP_ACTION_PERMS_MAP[wpAction]
  const users = await getSameBuildingUsers(buildingId, tenancyType, false, perm)
  if (excludeCurrent && data.managerId) {
    lodash.remove(users, user => user.id === data.managerId)
  }
  return users
    .reduce((partialData, user) => {
      const name = user.name
      const curUser = {
        displayPicName: `${name} (${user.username})`,
        id: user.id,
        email: user.email,
        name,
      }
      return partialData.concat([curUser])
    }, [])
    .sort((a, b) => a.displayPicName.localeCompare(b.displayPicName))
}

export function getHoistingExceedData (workPermit, draftHoistingFloors, type, buildingId) {
  const isTenantProject = workPermit.isTenantProject
  const standalone = workPermit.standalone
  const currentTenantProject = store.getters['tenantProject/currentTenantProject']

  return {
    contractorId: workPermit.contractorId,
    draftHoistingFloors,
    type,
    buildingId,
    isTenantProject,
    standalone,
    projectId: workPermit.projectId,
    tenancyId: standalone ? lodash.get(workPermit, 'sourceId') : lodash.get(currentTenantProject, 'tenancyId'),
  }
}

export function getDuration (wp) {
  const startDate = wp.startDate ? dateUtil.formatDate(wp.startDate, true) : null
  const endDate = wp.endDate ? dateUtil.formatDate(wp.endDate, true) : null
  return `${startDate || i18n.t('emptyValue')} - ${endDate || i18n.t('emptyValue')}`
}

export function getWorkerInfoLastUpdatedText (workPermit, translate = i18n.t.bind(i18n)) {
  const histories = lodash.orderBy(lodash.get(workPermit, 'histories', []), 'createdAt').reverse()
  const latestWorkerUpdate = histories.find(history => {
    return history.status === 'UPDATE_WORKERS' || history.status === WORK_PERMIT_STATUS.WAPPR
  })
  const displayName = lodash.get(latestWorkerUpdate, 'who.displayName', '')
  if (!displayName) return ''

  const updateTime = dateUtil.formatDate(latestWorkerUpdate.createdAt, true)
  return translate('lastUpdatedBy', [displayName, updateTime])
}

export function maskStatusForExternalUser (status) {
  const shouldBeMasked = [
    WORK_PERMIT_STATUS.ACKNOWLEDGED,
    WORK_PERMIT_STATUS.REVIEWED,
    WORK_PERMIT_STATUS.REVIEWED2,
  ]
  if (shouldBeMasked.includes(status)) {
    return WORK_PERMIT_STATUS.WAPPR
  }
  return status
}

export function filterInvalidWPInDashboard (items) {
  // When project equals null, means project status is incorrect like complete.Should be excluded
  return items.filter(item => {
    return !item.standalone && !item.isTenantProject ? (item.project !== null) : true
  })
}

export function findIndexOfElementBySelector (target, parent, selector) {
  const elements = parent.querySelectorAll(selector)
  for (let i = 0; i < elements.length; i++) {
    if (target === elements[i]) return i
  }
}

export function calcYesterday (date) {
  const time = (date ? new Date(date) : new Date()).getTime() - dateUtil.ONE_DAY_MILLI - 1
  return new Date(time)
}

export function getIsProjectAvailable ({isTenantProject, project, isEngineer}) {
  const status = lodash.get(project, 'status')

  if (!isTenantProject) {
    const isValidInCompleted = isEngineer ? project.dlpDate && status === projectStatus.COMPLETED : validateDlpInCompleted(project)
    return status === projectStatus.ACTIVE || isValidInCompleted
  } else {
    return status && ![
      tenantProjectStatus.WITHDRAWN,
      tenantProjectStatus.COMPLETED,
    ].includes(status)
  }
}

export function canProcessCurrentWP (permType) {
  const workPermit = store && store.getters['workPermit/currentWorkPermit']
  return engineerCanTakeAction(workPermit, permType)
}

function engineerCanTakeAction (workPermit, permType) {
  const isEngineer = store && store.getters['user/isEngineer']
  if (!isEngineer) return false

  const wpBuildingCode = getBuilding(workPermit, false)
  const userBuildingCodes = (store && store.getters['user/buildings']) || []
  const hasBuildingPermission = userBuildingCodes.includes(wpBuildingCode)

  return hasBuildingPermission && aclHelper.getAccess(permType)
}

export function isWorkPermitEditable (permit) {
  const editableStatuses = [WORK_PERMIT_STATUS.REJECTED, WORK_PERMIT_STATUS.DRAFT]
  return editableStatuses.includes(permit.status)
}

export function getProjectType (item) {
  let projectType = i18n.t('workPermit.projectTypes.standalone')
  if (!item.standalone) {
    projectType = item.isTenantProject
      ? i18n.t(`workPermit.projectTypes.${lodash.get(item, 'project.type').toLocaleLowerCase()}`)
      : WP_PROJECT_TYPE_MAP[lodash.get(item, 'project.numberType')] || ''
  }
  return projectType
}

export function parsedWPFieldToShow (item) {
  const emptyValue = i18n.t('emptyValue')

  let location = item.isTenantProject ? lodash.get(item, 'project.projectLocation', emptyValue) : item.location || emptyValue
  location = Array.isArray(location) ? location.join(',') : location
  if (item.type === WORK_PERMIT_TYPES.HOISTING) {
    location += ` (${item.workArea})`
  }

  const building = getBuilding(item, false)
  const floors = lodash.sortBy(item.floors || [], value => {
    return getFloorIndex(building, value)
  })
  const showedFloors = item.isAllFloors ? i18n.t('workPermit.allFloors') : floors.join(', ')
  const floorIndex = getFloorIndex(building, floors[0])

  const usernamePIC = lodash.get(item, 'project.manager.username')
  const displayNamePIC = lodash.get(item, 'project.manager.displayName')
  const PIC = item.isTenantProject ? emptyValue : userHelper.getUserName(usernamePIC, displayNamePIC)

  let tenantName = emptyValue
  if (item.isTenantProject) {
    tenantName = item.standalone
      ? lodash.get(item, 'tenancy.company.name')
      : lodash.get(item, 'project.tenancy.company.name')
  }

  return {
    projectType: getProjectType(item),
    location,
    building,
    floors: showedFloors,
    floorIndex,
    PIC,
    tenantName,
  }
}

export function isStandaloneWpContractor (permit) {
  if (!permit || !permit.contractorId || !permit.standalone) return false

  const currentUser = store && store.getters['user/currentUser']
  return currentUser && (currentUser.externalId === permit.contractorId)
}

export function isStandaloneWpTenant (permit, currentUser) {
  return lodash.get(permit, 'tenancy.accounts', []).some(
    (account) => !lodash.isEmpty(account.TenancyAccount.perms.StandaloneWorkPermits) &&
      account.id === currentUser.id)
}

export function isStandaloneWpTenancyContractor (permit, currentUser) {
  return lodash.get(permit, 'tenancy.tenancyContractors', [])
    .some(item => item.userId === currentUser.id && (permit.contractorId === item.companyId))
}

export function composeNewHoistingPayload (permit) {
  const payload = {
    type: WORK_PERMIT_TYPES.HOISTING,
    contractorId: permit.contractorId,
    isTenantProject: permit.isTenantProject,
    parentId: permit.id,
  }
  if (permit.standalone) {
    payload.standalone = true
    if (permit.isTenantProject) {
      payload.sourceId = permit.sourceId
    }
  } else {
    payload.projectId = permit.projectId
  }
  return payload
}

/**
 * author: alvis
 * timestamp: 2026-04-23
 * description: Reads the raw `who` from a history row. Some list/detail payloads mirror Sequelize: only `dataValues.who` is set, with no top-level `who`.
 * @param {Object|undefined} row
 * @returns {string|Object|null|undefined} Raw `who` value, or null when missing
 */
export function getWorkPermitHistoryWhoField (row) {
  if (row == null || typeof row !== 'object') {
    return null
  }
  if (row.who != null) {
    return row.who
  }
  if (row.dataValues && row.dataValues.who != null) {
    return row.dataValues.who
  }
  return null
}

/**
 * author: alvis
 * timestamp: 2026-04-23
 * description: Some work permit list/detail API payloads return Sequelize-style rows: `status` only on `dataValues`, not the top level. WPCollapseContent and groupBy need this so `APPR` etc. keys match `WORK_PERMIT_STATUS`.
 * @param {Object|undefined} row - A work permit `histories` element
 * @returns {string|undefined}
 */
export function getWorkPermitHistoryRowStatus (row) {
  if (row == null || typeof row !== 'object') {
    return undefined
  }
  if (row.status != null && row.status !== '') {
    return row.status
  }
  if (row.dataValues && row.dataValues.status != null && row.dataValues.status !== '') {
    return row.dataValues.status
  }
  return undefined
}

/**
 * Read history row created time from top-level or Sequelize-style dataValues.
 * @param {Object|undefined} row
 * @returns {*}
 */
export function getWorkPermitHistoryCreatedAt (row) {
  if (row == null || typeof row !== 'object') {
    return null
  }
  if (row.createdAt != null && row.createdAt !== '') {
    return row.createdAt
  }
  if (row.dataValues && row.dataValues.createdAt != null && row.dataValues.createdAt !== '') {
    return row.dataValues.createdAt
  }
  return null
}

/**
 * author: alvis
 * timestamp: 2026-04-23
 * description: Some API responses return `histories[].who` as a JSON string (e.g. Sequelize / MSSQL), not a plain object. Parse so UI can read displayName and username.
 * @param {*} who - Raw work permit history `who` (string, object, or from getWorkPermitHistoryWhoField)
 * @returns {Object|null} Actor object, or null if unparseable
 */
export function parseWorkPermitHistoryWho (who) {
  if (who == null) return null
  if (typeof who === 'object' && !Array.isArray(who)) {
    return who
  }
  if (typeof who === 'string' && who.trim() !== '') {
    try {
      const parsed = JSON.parse(who)
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        return parsed
      }
    } catch (e) {
      return null
    }
  }
  return null
}

/**
 * author: alvis
 * timestamp: 2026-04-23
 * description: Resolves a parsed actor from a full history row or a raw `who` value. Prefer passing the full row so `dataValues.who` is not missed.
 * @param {*} historyRowOrWho - A history object from `permit.histories`, or a raw `who` field
 * @returns {Object|null} Actor object for display, or null
 */
export function getParsedWorkPermitHistoryActor (historyRowOrWho) {
  if (historyRowOrWho == null) {
    return null
  }
  if (typeof historyRowOrWho === 'object' && !Array.isArray(historyRowOrWho)) {
    if (historyRowOrWho.who != null || (historyRowOrWho.dataValues && historyRowOrWho.dataValues.who != null)) {
      return parseWorkPermitHistoryWho(getWorkPermitHistoryWhoField(historyRowOrWho))
    }
  }
  return parseWorkPermitHistoryWho(historyRowOrWho)
}

export function parseSearchFilterProjectType (projectType) {
  // `projectType: undefined` to override value in filters
  let result = { projectType: undefined }

  if (WP_PROJECT_TYPES.includes(projectType)) {
    projectType === WP_SA ? result.standalone = true : result.projectType = projectType
  }

  return result
}

export function disableTenantContractorEdit () {
  const currentTenantProject = store.getters['tenantProject/currentTenantProject']

  return !!(currentTenantProject.extSys || []).length
}

export function showHoistingByModuleNHoisting (hoisting = []) {
  const backEndRegionalEnv = store.getters['regions/backEndRegionalEnv']
  return lodash.get(backEndRegionalEnv, 'moduleSwitch.hoistingModule') || hoisting.length
}

export { formatWorkPermitHistories, formatWorkPermitHistoriesForViewer, WP_LIFECYCLE_COMMENT_HTML_KEY }
